import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { analysis, type Portfolio } from 'venn-api';
import { createHistoricalPortfolioSnapshotKey } from './query-keys';
import type AnalysisSubject from '../analysis/AnalysisSubject';

type QueryOptions = Pick<UseQueryOptions, 'suspense' | 'enabled'>;

export class HistoricalPortfolioSnapshotNotFoundError extends Error {
  constructor(
    public readonly portfolio: Portfolio,
    public readonly allocationsAsOfDate: number,
  ) {
    super('Historical portfolio snapshot not found');
    Object.setPrototypeOf(this, HistoricalPortfolioSnapshotNotFoundError.prototype);
  }
}

export const useHistoricalPortfolioSnapshotQuery = (
  {
    subject,
    allocationsAsOfDate,
  }: {
    subject: AnalysisSubject | undefined;
    allocationsAsOfDate: number | undefined;
  },
  options: QueryOptions = {},
  analysisViewId?: string,
): UseQueryResult<Portfolio> => {
  const portfolioId = subject?.id;
  return useQuery<Portfolio>(
    createHistoricalPortfolioSnapshotKey(subject, allocationsAsOfDate),
    async () => {
      // Normally they should not be undefined, but because we can't call hooks conditionally
      // we need to allow for undefined to be passed here and do this check
      if (!portfolioId || !allocationsAsOfDate) {
        return Promise.reject(new Error('Portfolio ID and allocations as of date are required'));
      }
      if (!subject?.portfolio?.historical) {
        return Promise.reject(new Error('Snapshot can only be fetched for historical portfolios'));
      }

      const response = await analysis({
        analyses: [
          {
            analysisType: 'HISTORICAL_PORTFOLIO_SNAPSHOT',
            historicalPortfolioAsOfDate: allocationsAsOfDate,
          },
        ],
        subjects: [
          {
            comparisonType: 'PRIMARY',
            id: portfolioId.toString(),
            isPrivate: false,
            primary: true,
            subjectType: 'PORTFOLIO',
            proxySetId: analysisViewId ?? null,
          },
        ],
      });

      const result = response.content.analyses[0]?.historicalPortfolioSnapshots?.[0]?.snapshot;
      if (!result) {
        return Promise.reject(new HistoricalPortfolioSnapshotNotFoundError(subject?.portfolio, allocationsAsOfDate));
      }
      return result;
    },
    options,
  );
};
